/**
 * web/src/lib/approvalKinds.ts
 * The approval-kind registry (design_handoff_speaker_approvals): one entry per
 * speaker human_gate step. The Approvals surface is driven ENTIRELY off this
 * registry — the kind strip, row verbs, consequence copy, and the review
 * queue's headers all read from here, so adding or renaming a kind requires
 * no layout change.
 *
 * `what` is product copy the operator relies on INSTEAD of opening the item —
 * keep it honest against the step's real onApproval behavior
 * (src/pixel/workflows/speaker/definition.py).
 */
import { getStepDefs } from './workflow-step-defs';

/**
 * Which review body the queue renders for a kind. Card gates left this surface
 * for the Speaker Cards pipeline board (/speakers/cards) on 2026-08-20 —
 * every remaining kind is an email gate.
 */
export type ApprovalPayload = 'email';

export interface ApprovalKind {
  /** Stable key — the speaker workflow stepId. */
  id: string;
  /** Noun phrase for filters, groups, eyebrows. */
  label: string;
  /** Imperative phrase used as the row headline and queue title. */
  verb: string;
  /** One plain-language sentence: the consequence of approving. */
  what: string;
  /** Why Skip is safe — the queue aside's "Not blocking" line. */
  notBlocking: string;
  /** Selects the queue's review body. */
  payload: ApprovalPayload;
  /** Zero-based index in the speaker workflow spine. */
  stepIndex: number;
  /** Band A (sequential head) = 0, band B (armed tail) = 1. */
  band: 0 | 1;
  /**
   * Steps in this kind's workflow, for the queue's "Step N of M" tag. Absent =
   * the speaker workflow (SPEAKER_STEP_COUNT). Sponsor kinds set it.
   */
  stepCount?: number;
}

const SPEAKER_STEP_IDS = getStepDefs('speaker_onboarding').map((s) => s.id);

/** stepIndex from the canonical spine so the registry can't drift from it. */
function idx(stepId: string): number {
  const i = SPEAKER_STEP_IDS.indexOf(stepId);
  if (i === -1) throw new Error(`approvalKinds: unknown speaker step '${stepId}'`);
  return i;
}

/**
 * Registry order = workflow order. Kinds with zero pending still render in
 * the strip (dimmed) so the operator learns the full taxonomy.
 */
export const APPROVAL_KINDS: ApprovalKind[] = [
  {
    id: 'invite',
    label: 'Invitation',
    verb: 'Send invitation',
    what: "Sends the invitation email and starts watching for the speaker's reply.",
    notBlocking: 'Nothing sends until you approve — the speaker simply stays uninvited.',
    payload: 'email',
    stepIndex: idx('invite'),
    band: 0,
  },
  {
    id: 'confirmation',
    label: 'Confirmation & handoff',
    verb: 'Send confirmation & handoff',
    what: 'Confirms the slot, requests headshot + bio, and marks the board Invite Accepted.',
    notBlocking: 'The speaker already said yes — a short delay here only delays the asset chase.',
    payload: 'email',
    stepIndex: idx('confirmation'),
    band: 0,
  },
  {
    id: 'prep_notes',
    label: 'Prep call notes',
    verb: 'Send prep call notes',
    what: 'Sends the prep-call recap notes to the speaker.',
    notBlocking: 'Notes are a courtesy follow-up — nothing downstream waits on them.',
    payload: 'email',
    stepIndex: idx('prep_notes'),
    band: 1,
  },
  // card_assets deliberately absent: speaker cards are reviewed on the Speaker
  // Cards pipeline board (/speakers/cards), not in this queue (2026-08-20).
  {
    id: 'promotion',
    label: 'Speaker promotion',
    verb: 'Send promotion email',
    what: 'Sends the promotion email with the speaker card attached for self-promotion.',
    notBlocking: 'Skipping only delays their self-promotion window; no other step waits on it.',
    payload: 'email',
    stepIndex: idx('promotion'),
    band: 1,
  },
  {
    id: 'logistics',
    label: 'Final logistics',
    verb: 'Send logistics email',
    what: 'Sends travel, hotel, and run-of-show details to the speaker.',
    notBlocking: 'No other step waits on this — but the speaker is unbriefed until it sends.',
    payload: 'email',
    stepIndex: idx('logistics'),
    band: 1,
  },
  {
    id: 'thank_you',
    label: 'Post-event thank-you',
    verb: 'Send thank-you',
    what: 'Sends the post-event thank-you note.',
    notBlocking: 'Post-event courtesy — nothing depends on it.',
    payload: 'email',
    stepIndex: idx('thank_you'),
    band: 1,
  },
];

export const KIND_BY_ID: ReadonlyMap<string, ApprovalKind> = new Map(
  APPROVAL_KINDS.map((k) => [k.id, k]),
);

/** Total steps in the speaker spine (for "Step N of M" tags). */
export const SPEAKER_STEP_COUNT = SPEAKER_STEP_IDS.length;
